import bgmyprofile from "@/assets/Image2.png";
import CustomInput from "./CustomInput";
import { Button } from "../ui/button";
import { Form } from "../ui/form";
import { MAX_FILE_SIZE, myProfileFormSchema } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { useEffect, useState } from "react";
import {
  AlertCircle,
  CheckCircle,
  Loader2,
  UploadCloud,
  XCircle,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchUserDetails, updateUserDetail } from "@/api/apiFunction";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "../ui/label";
import { BASE_URL, Image_BASE_URL } from "@/api/apiUrl";
import { useToast } from "@/components/hooks/use-toast";
import axios from "axios";
import { useStore } from "@/lib/userDetails/UserDetails";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
// @ts-ignore
import { Badge } from "@/components/ui/badge";
import { toast } from "react-hot-toast";
import moment from "moment";

interface MyProfileFormProps {
  data: any; // Replace 'any' with the appropriate type if known
}
interface Country {
  name: string;
}
interface StateType {
  name: string;
}
interface City {
  name: string;
}

// Fetch countries
const getCountriesData = async (): Promise<Country[]> => {
  const response = await axios.get(`${BASE_URL}/address-access/countries`);
  return response.data;
};

// Fetch states for the selected country
const getStatesData = async (country: string): Promise<StateType[]> => {
  if (!country) return [];
  const response = await axios.get(
    `${BASE_URL}/address-access/states/${country}`
  );
  return response.data;
};

// Fetch cities for the selected state
const getCitiesData = async (state: string): Promise<City[]> => {
  if (!state) return [];
  const response = await axios.get(
    `${BASE_URL}/address-access/cities/${state}`
  );
  return response.data;
};

export const genderOptions = [
  { label: "Male", value: "MALE" },
  { label: "Female", value: "FEMALE" },
  { label: "Other", value: "OTHER" },
];
const MyProfileForm = ({ data }: MyProfileFormProps) => {
  const [profilePic, setProfilePic] = useState<string | null>(null);
  const [aadharFile, setAadharFile] = useState<File | null>(null);
  const formSchema = myProfileFormSchema();
  const [country, setCountry] = useState(
    data?.data?.user_detail?.country || "India"
  );
  const backendDate = data?.data?.user_detail?.birthDate;
  const initialDate =
    backendDate && !isNaN(Date.parse(backendDate))
      ? new Date(backendDate)
      : null;
  const [date, setDate] = useState<Date | null>(initialDate);

  const { setUser, setAuthenticated } = useStore();
  const [state, setState] = useState(data?.data?.user_detail?.state || "");

  const { toast: soonerToast } = useToast();
  const queryClient = useQueryClient();

  // 1. Define Form
  const form = useForm<z.infer<typeof formSchema> & { email: string }>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: data?.data?.name ? data?.data?.name : "",
      dateOfBirth: data?.data?.user_detail?.birthDate
        ? data?.data?.user_detail?.birthDate
        : "",
      profileImage: data?.data?.profileImage ? data?.data?.profileImage : "",
      gender: data?.data?.user_detail?.gender
        ? data?.data?.user_detail?.gender
        : "",
      bloodGroup: data?.data?.user_detail?.bloodGroup
        ? data?.data?.user_detail?.bloodGroup
        : "",
      email: data?.data?.email ? data?.data?.email : "",
      mobile: data?.data?.mobile ? data?.data?.mobile : "",
      jerseySize: data?.data?.user_detail?.jerseySize
        ? data?.data?.user_detail?.jerseySize
        : "",
      city: data?.data?.user_detail?.city ? data?.data?.user_detail?.city : "",
      state: data?.data?.user_detail?.state
        ? data?.data?.user_detail?.state
        : "",
      country: data?.data?.user_detail?.country
        ? data?.data?.user_detail?.country
        : "India",
      address: data?.data?.user_detail?.address
        ? data?.data?.user_detail?.address
        : "",
      pinCode: data?.data?.user_detail?.pinCode
        ? data?.data?.user_detail?.pinCode
        : "",
      emergencyContactName: data?.data?.user_detail?.emergencyContactName
        ? data?.data?.user_detail?.emergencyContactName
        : "",
      emergencyContactNumber: data?.data?.user_detail?.emergencyContactNumber
        ? data?.data?.user_detail?.emergencyContactNumber
        : "",
      gstNumber: data?.data?.gstNumber ? data?.data?.gstNumber : "",
      aadharImage: data?.data?.aadharImage ? data.data.aadharImage : null,
    },
  });
  useEffect(() => {
    if (data) {
      form.reset({
        name: data?.data?.name || "",
        dateOfBirth: data?.data?.user_detail?.birthDate || "",
        profileImage: data?.data?.profileImage || "",
        gender: data?.data?.user_detail?.gender || "",
        bloodGroup: data?.data?.user_detail?.bloodGroup || "",
        email: data?.data?.email || "",
        mobile: data?.data?.mobile || "",
        jerseySize: data?.data?.user_detail?.jerseySize || "",
        city: data?.data?.user_detail?.city || "",
        state: data?.data?.user_detail?.state || "",
        country: data?.data?.user_detail?.country || "India",
        address: data?.data?.user_detail?.address || "",
        pinCode: data?.data?.user_detail?.pinCode || "",
        emergencyContactName:
          data?.data?.user_detail?.emergencyContactName || "",
        emergencyContactNumber:
          data?.data?.user_detail?.emergencyContactNumber || "",
        gstNumber: data?.data?.gstNumber || "",
        aadharImage: data?.data?.aadharImage || null,
      });
      if (date !== null) {
        setDate(new Date(data?.data?.user_detail?.birthDate));
      }
      // Update profilePic and aadharFile state with the new data as well
      // setProfilePic(data?.data?.profileImage || null);
      setAadharFile(null); // reset file selection, or keep the uploaded file if needed
    }
  }, [data, form]); // Update form values when data changes

  useEffect(() => {
    if (localStorage.getItem("profileUpdated") === "true") {
      toast.success("Profile updated successfully");
      localStorage.removeItem("profileUpdated");
    }
  }, []);
  //2. define submit handler
  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    try {
      const userData = {
        //@ts-ignore
        userId: data.userId, // Add userId here
        name: data.name,
        birthDate: data.dateOfBirth,
        profileImage:
          typeof data.profileImage === "string" ? undefined : data.profileImage,
        gender: data.gender,
        email: data.email,
        mobile: data.mobile,
        jerseySize: data.jerseySize,
        bloodGroup: data.bloodGroup,
        city: data.city,
        state: data.state,
        country: data.country,
        address: data.address,
        pinCode: data.pinCode,
        emergencyContactName: data.emergencyContactName,
        emergencyContactNumber: data.emergencyContactNumber,
        gstNumber: data.gstNumber,
        aadharImage: data.aadharImage || "",
        phoneCode: "+91",
      };

      mutate(userData);
      // console.log("userData", userData);
    } catch (error) {
      // console.log(error);
      toast.error("Error updating user details");
    }
  };

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        alert("File size should be under 5MB");
        return;
      }
      setProfilePic(URL.createObjectURL(file)); // Preview the image
      form.setValue("profileImage", file); // Set the File object in form
    } else {
      form.setValue("profileImage", undefined); // Reset if no file is selected
    }
  };

  const handleAadharChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (data?.data?.status === "VERIFIED") {
      alert(
        "You can't update your aadhar card details as it is already approved"
      );
      return;
    }
    const file = e.target.files?.[0];
    if (file) {
      const isImage = file.type.startsWith("image/");
      const isPdf = file.type === "application/pdf";
      if (!isImage && !isPdf) {
        alert("Only image and pdf files are supported");
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        alert("File size should be under 5MB");
        return;
      }
      setAadharFile(file);
      form.setValue("aadharImage", file);
    }
  };

  // on form submition
  const { mutate, isPending } = useMutation({
    mutationFn: updateUserDetail,
    onSuccess: () => {
      localStorage.setItem("profileUpdated", "true");
      toast.success("Profile updated successfully");
      setTimeout(() => {
        // 4. Update queries and reload
        queryClient.invalidateQueries({ queryKey: ["user-details"] });
        fetchUserDetails(setAuthenticated, setUser);
        window.location.reload();
      }, 1000);
    },
    onError: (error) => {
      // console.error("Error submitting form:", error);
      soonerToast({
        variant: "destructive",
        title: "Uh oh! Something went wrong.",
        //@ts-ignore
        description: `${error?.response?.data?.message || "An error occurred"}`,
      });
    },
  });

  const { data: countries = [] } = useQuery<Country[]>({
    queryKey: ["countries"],
    queryFn: getCountriesData,
    staleTime: Infinity,
  });

  const { data: states = [], isLoading: stateLoading } = useQuery<StateType[]>({
    queryKey: ["states", form.watch("country"), country],
    queryFn: () => getStatesData(country),
    enabled: !!country,
    staleTime: Infinity,
  });
  const { data: cities = [], isLoading: cityLoading } = useQuery<City[]>({
    queryKey: ["cities", form.watch("state")],
    queryFn: () => getCitiesData(state),
    enabled: !!state,
    staleTime: Infinity,
  });

  const handleCountryChange = (value: string) => {
    form.setValue("country", value);
    form.setValue("state", "");
    form.setValue("city", "");
    setCountry(value);
    queryClient.invalidateQueries({ queryKey: ["states"] });
    queryClient.invalidateQueries({ queryKey: ["cities"] });
  };

  const handleStateChange = (value: string) => {
    form.setValue("state", value);
    form.setValue("city", "");
    setState(value);
    queryClient.invalidateQueries({ queryKey: ["cities"] });
  };

  useEffect(() => {
    if (form.formState.errors.profileImage) {
      alert("Please upload your profile image");
    }
  }, [form.formState.errors.profileImage]);
  return (
    <section className="w-full pt-32 lg:pt-32 mx-auto">
      <div>
        <img
          src={bgmyprofile}
          alt=""
          width={800}
          height={800}
          className="w-full h-96 md:h-full object-cover"
        />
      </div>
      <div className="max-w-6xl mb-20 px-4 md:px-0 mx-auto">
        <Form {...form}>
          <form
            className="space-y-8 border mb-44  md:mb-52 bg-white  md:max-w-4xl -mt-80 md:-mt-80 lg:z-40 left-0  px-6 py-7 rounded-lg md:left-[20rem] mx-auto overflow-x-hidden w-full drop-shadow-lg"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <div className="w-full flex flex-col gap-6 mx-auto ">
              <h1 className="text-center text-3xl md:text-4xl font-semibold text-[#009FA6] mb-6 ">
                My Profile
              </h1>

              <div className="w-full  flex flex-col-reverse items-center  md:flex-row gap-6  justify-between">
                <div className=" flex flex-col gap-6 w-full">
                  <CustomInput
                    control={form.control}
                    name="name"
                    label="Full Name"
                    placeholder="your name"
                  />

                  <div className="flex flex-col md:flex-row w-full justify-between gap-6">
                    <div className="w-full flex flex-col md:block">
                      <Label htmlFor="dateOfBirth">Date of Birth</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-[240px] justify-start text-left font-normal",
                              !date && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon />

                            {date !== null && moment(date).isValid() ? (
                              moment(date).format("DD/MM/YYYY")
                            ) : (
                              <span>Date of Birth</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            name="dateOfBirth"
                            mode="single"
                            captionLayout="dropdown-buttons"
                            selected={date}
                            onSelect={(selectedDate: Date) => {
                              setDate(selectedDate);
                              const formattedDate = selectedDate
                                ? moment(selectedDate).format("YYYY-MM-DD")
                                : "";

                              if (!moment(formattedDate).isValid()) {
                                // toast.error("Invalid date format");
                                return;
                              }
                              form.setValue("dateOfBirth", formattedDate);
                            }}
                            fromYear={1960}
                            toYear={2030}
                            control={form.control}
                            classNames={{
                              // months: "flex flex-col",
                              caption:
                                "ml-2 flex justify-between w-56 p-2 items-center mb-4",
                              nav_button: "w-5 h-5 ",
                            }}
                          />
                        </PopoverContent>
                      </Popover>
                      {form.formState.errors.dateOfBirth && (
                        <p className="text-red-500">
                          {form.formState.errors.dateOfBirth.message}
                        </p>
                      )}
                    </div>
                    <div className="w-full ">
                      <CustomInput
                        control={form.control}
                        name="bloodGroup"
                        label="Blood Group"
                        type="select"
                        placeholder="Select Blood Group"
                        options={[
                          "A+",
                          "A-",
                          "B+",
                          "B-",
                          "AB+",
                          "AB-",
                          "O+",
                          "O-",
                        ]}
                      />
                    </div>
                  </div>

                  <CustomInput
                    className="ml-6"
                    control={form.control}
                    name="gender"
                    label="Gender"
                    type="radio"
                    options={genderOptions}
                  />
                </div>
                <div className="flex-col justify-end items-end space-y-2  w-52">
                  {/* image */}
                  <div className=" mx-auto relative aspect-[1] overflow-hidden w-48 h-56 border-2 border-black border-dashed">
                    <label
                      className="w-full h-full grid place-items-center cursor-pointer relative"
                      htmlFor="profile_pic"
                    >
                      {(profilePic || data?.data?.profileImage) && (
                        <a
                          href={
                            profilePic ||
                            `${Image_BASE_URL}/images/profile/${data?.data?.profileImage}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full h-full"
                        >
                          <img
                            src={
                              profilePic ||
                              `${Image_BASE_URL}/images/profile/${data?.data?.profileImage}`
                            } // Show URL if no file is uploaded
                            alt=""
                            className="w-full h-[14rem] object-cover"
                          />
                          {profilePic && (
                            <img
                              src={profilePic}
                              className="w-full h-full object-cover z-10"
                            />
                          )}
                        </a>
                      )}

                      {profilePic && (
                        <img
                          src={profilePic}
                          className="w-full h-full object-cover z-10"
                        />
                      )}
                      <div className=" flex flex-col absolute top-16 left-8 z-0 justify-center items-center text-primary">
                        <UploadCloud className="opacity-0" size={50} />
                        <div className="text-md opacity-0">
                          Upload Your <br />
                          company logo
                        </div>
                      </div>
                    </label>

                    <input
                      type="file"
                      id="profile_pic"
                      className="w-full sr-only"
                      onChange={handleProfileChange}
                    />
                  </div>
                  <div className="text-center">
                    <p className="text-xs  w-52 text-[#00000066]">
                      Image size should be under 5MB and image ration needs to
                      be 1:1
                    </p>
                  </div>
                </div>
              </div>
              <CustomInput
                control={form.control}
                name="email"
                label="Email"
                placeholder="example@example.com"
                disabled={form.watch("email") === data?.data?.email}
              />

              <CustomInput
                control={form.control}
                name="mobile"
                label="Mobile"
                placeholder="+91xxxxxxxxxx"
                disabled={form.watch("mobile") === data?.data?.mobile}
              />

              <div className="">
                <CustomInput
                  control={form.control}
                  name="jerseySize"
                  label="T-Shirt / Jersey"
                  type="radio"
                  className="grid grid-cols-4 mt-2  md:flex md:flex-row  md:space-x-6" // For horizontal alignment
                  options={["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"]}
                />
              </div>
              <div className="w-full flex flex-col md:flex-row gap-6">
                <div className="w-full">
                  <CustomInput
                    control={form.control}
                    name="country"
                    label="Country"
                    type="select"
                    placeholder="Select"
                    options={countries.map((c) => c.name)}
                    onChange={handleCountryChange}
                  />
                </div>
                <div className="w-full">
                  <CustomInput
                    control={form.control}
                    name="state"
                    label="State"
                    type="select"
                    placeholder="Select"
                    options={states.map((s) => s.name)}
                    onChange={handleStateChange}
                    disabled={stateLoading}
                  />
                </div>
                <div className="w-full">
                  <CustomInput
                    control={form.control}
                    name="city"
                    label="City"
                    type="select"
                    placeholder="Select"
                    options={cities.map((c) => c.name)}
                    disabled={cityLoading}
                  />
                </div>
              </div>

              <div>
                <Label>Address</Label>
                <Controller
                  name="address"
                  control={form.control}
                  render={({ field }) => (
                    <Textarea
                      {...field}
                      //@ts-ignore
                      placeholder="Enter your address"
                      className="my-custom-class" // You can customize this class
                    />
                  )}
                />
              </div>

              <CustomInput
                control={form.control}
                name="pinCode"
                label="Pincode / Zipcode / Post code"
                placeholder=""
              />

              <div className="w-full flex flex-col md:flex-row gap-10">
                <div className="w-full ">
                  <CustomInput
                    control={form.control}
                    name="emergencyContactName"
                    label="Emergency Contact Name"
                    placeholder=""
                    className=""
                  />
                </div>
                <div className="w-full ">
                  <CustomInput
                    control={form.control}
                    name="emergencyContactNumber"
                    label="Emergency Contact Number"
                    placeholder="emergency mobile number"
                  />
                </div>
              </div>
              <div className="relative md:w-[25rem]">
                <span className=" ">
                  <p className="absolute left-[90px] -top-[2px] text-xs mt-2 text-[#00000066]">
                    (*GST number is optional)
                  </p>
                </span>
                <CustomInput
                  control={form.control}
                  name="gstNumber"
                  label="GST Number"
                  placeholder=""
                />
              </div>

              <h2 className="mt-6 text-xl font-semibold">
                Upload Your Aadhar Card
              </h2>

              <div className="mx-auto md:w-full rounded-xl py-3 md:py-5 border-gray-300">
                <label className="cursor-pointer w-44 h-42 block">
                  {aadharFile || data?.data?.aadharImage ? (
                    <div className="mb-4 mt-6">
                      {aadharFile ? (
                        aadharFile.type === "application/pdf" ? (
                          <a
                            href={URL.createObjectURL(aadharFile)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 underline"
                          >
                            View Uploaded PDF
                          </a>
                        ) : (
                          <img
                            src={URL.createObjectURL(aadharFile)}
                            alt="Aadhar Preview"
                            style={{
                              width: "165px",
                              height: "100px",
                              borderRadius: "8px",
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                            }}
                          />
                        )
                      ) : data?.data?.aadharImage.endsWith(".pdf") ? (
                        <a
                          href={`${Image_BASE_URL}/images/aadhar/${data.data.aadharImage}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 underline"
                        >
                          View Existing PDF
                        </a>
                      ) : (
                        <a
                          href={
                            profilePic ||
                            `${Image_BASE_URL}/images/aadhar/${data.data.aadharImage}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full h-full"
                        >
                          <img
                            src={`${Image_BASE_URL}/images/aadhar/${data.data.aadharImage}`}
                            alt="Aadhar Preview"
                            style={{
                              width: "165px",
                              height: "100px",
                              borderRadius: "8px",
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                            }}
                          />
                        </a>
                      )}
                    </div>
                  ) : null}
                </label>
                <input
                  type="file"
                  id="aadhar_upload"
                  className="mt-2"
                  accept="image/*,application/pdf"
                  onChange={handleAadharChange}
                />
                {aadharFile ? (
                  <div className="mt-2 text-left text-md text-gray-500">
                    {/* {aadharFile.name} */}
                  </div>
                ) : (
                  <div className="text-left text-md text-gray-500">
                    Select a file
                    <p className="text-xs">
                      JPG, PNG, or PDF, file size no more than 5MB
                    </p>
                  </div>
                )}

                {form.formState.errors.aadharImage ? (
                  <p className="text-red-500">Please upload your Aadhar card</p>
                ) : aadharFile === null ? (
                  data?.data?.aadharImage === null ? (
                    <p className="text-red-500">
                      {form.formState.errors.aadharImage}
                    </p>
                  ) : null
                ) : null}
              </div>
              <div className="flex items-center gap-2 mt-4">
                <span className="text-sm font-medium text-gray-700">
                  Aadhar Verification Status:
                </span>
                {data.data.status === "VERIFIED" && (
                  <Badge
                    variant="success"
                    className="px-3 py-1 text-xs font-semibold bg-green-100 text-green-800"
                  >
                    <CheckCircle className="w-3 h-3 mr-1 inline-block" />
                    Verified
                  </Badge>
                )}
                {data.data.status === "REJECTED" && (
                  <Badge
                    variant="destructive"
                    className="px-3 py-1 text-xs font-semibold bg-red-100 text-red-800"
                  >
                    <XCircle className="w-3 h-3 mr-1 inline-block" />
                    Rejected
                  </Badge>
                )}
                {data.data.status === "UNVERIFIED" && (
                  <Badge
                    variant="warning"
                    className="px-3 py-1 text-xs font-semibold bg-yellow-100 text-yellow-800"
                  >
                    <AlertCircle className="w-3 h-3 mr-1 inline-block" />
                    Unverified
                  </Badge>
                )}
              </div>
              <div className="flex flex-col justify-center  items-center gap-4">
                <Button
                  type="submit"
                  disabled={isPending}
                  className="form-btn w-36 font-semibold px-5 md:px-10 mt-7 rounded-3xl bg-gradient-to-r from-[#4BD5D6] to-[#009FA6]"
                >
                  {isPending ? (
                    <>
                      <Loader2 size={20} className="animate-spin" /> &nbsp;
                      Loading...
                    </>
                  ) : (
                    "Update Profile"
                  )}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </div>
    </section>
  );
};

export default MyProfileForm;

